import React, { useEffect } from 'react'

import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'

import HFOptionGroup from 'components/HFInputs/HFOptionGroup'

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm, FormProvider } from 'react-hook-form'
import { causalLinkOpinionSchema } from './CausalLinkOpinionSchema'

import FinalizeTaskDialog from '../../FinalizeTaskController/FinalizeTaskDialog'
import useSubmitTask from 'hooks/useSubmitTask'
import HFTextInput from 'components/HFInputs/HFTextInput'

import styles from '../../Dialog.module.sass'
import TaskHeaderController from 'controllers/TaskManager/TaskSubControllers/TaskHeader/TaskHeader'
import InfoList from 'components/InfoList/InfoList'
import { mapOpinionTaskDetails } from 'controllers/TaskManager/TaskSubControllers/TaskDetailsControllers/detailsUtils'
import TaskMenuController from '../../TaskMenu/TaskMenuController'
import AlertBox from 'components/AlertBox/AlertBox'
import { useFetchList } from 'store'
import MedicalOpinion from 'models/MedicalOpinion'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'

const CausalLinkEvaluationTaskController = (props) => {
	const { i18n } = useGlobal()
	const { dismiss, id, resolve } = props
	const { data } = props

	const opinionUri = `/metadata/medical_opinion/${data?.opinionRequest?.opinionId}`

	const { contents } = useFetchList(
		[data?.opinionRequest?.opinionId && opinionUri],
		[data?.opinionRequest?.opinionId && MedicalOpinion.fromJSON]
	)

	const [medicalOpinion] = contents

	const booleanOptions = [
		{
			value: true,
			text: i18n`Yes`,
		},
		{
			value: false,
			text: i18n`No`,
		},
	]

	const causalLinkOptions = [
		{
			value: 'valid',
			text: i18n`Yes`,
		},
		{
			value: 'invalid',
			text: i18n`No`,
		},
		{
			value: 'missing',
			text: i18n`Dubious`,
		},
	]

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(causalLinkOpinionSchema),
		shouldUnregister: true,
	})
	const { handleSubmit, watch, formState, reset, getValues } = methods

	const { error, submitting, approveTask } = useSubmitTask(
		data?.id,
		formState?.errors
	)

	const [actions, accident] = watch(['actions.value', 'accident.value'])

	const onSubmit = (data) => {
		approveTask(data, 'submit')
			.then(() => resolve())
			.catch(() => {})
	}

	const customError = error?.map((err) => {
		return {
			title: i18n`Error`,
			text: err,
		}
	})

	useEffect(() => {
		if (
			data.formState &&
			!formState.isDirty &&
			formState.submitCount === 0
		) {
			reset && data.formState && reset(data.formState)
		}
	}, [reset, data.formState, formState.isDirty, formState.submitCount])

	return (
		<>
			{submitting ? (
				<FinalizeTaskDialog
					dismiss={dismiss}
					submitting={submitting}
					task={data}
				/>
			) : (
				<>
					<FormProvider {...methods}>
						<div className={styles['form-container']}>
							<TaskHeaderController task={data} />
							<form
								id={`form_${id}`}
								className={styles['form']}
								onSubmit={handleSubmit(onSubmit)}
							>
								{customError && customError.length > 0 && (
									<AlertBox
										className={styles['alert-box']}
										type='error'
										items={customError}
									/>
								)}
								<InfoList
									items={mapOpinionTaskDetails(
										medicalOpinion,
										i18n
									)}
								/>

								<fieldset>
									<h4>{i18n`Injury Accident`}</h4>
									<VStack
										className={styles['option-group-list']}
									>
										<HFOptionGroup
											className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
											name='accident.value'
											label={i18n`Is there an injury mechanism that can be characterized as an accident?`}
											options={causalLinkOptions}
										/>

										{accident && accident !== 'invalid' && (
											<>
												<div>
													<h5>{i18n`Justification`}</h5>
													<HFTextInput
														name='accident.justification'
														placeholder={i18n`Justification`}
														multiline={true}
														rows={5}
														noResize
													/>
												</div>

												<HFOptionGroup
													className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
													name='diagnoses.value'
													label={i18n`Do the diagnoses made have a causal link with the described injury mechanism?`}
													options={causalLinkOptions}
												/>
											</>
										)}

										<div>
											<h5>{i18n`Causal Link Opinion`}</h5>
											<HFTextInput
												name='diagnoses.justification'
												placeholder={i18n`Causal Link Opinion`}
												multiline={true}
												rows={5}
												noResize
											/>
										</div>

										<div>
											<HFOptionGroup
												className={`${styles['option-group']} ${styles['--horizontal-flex']}`}
												name='actions.value'
												type='boolean'
												label={i18n`Suggested Actions?`}
												options={booleanOptions}
											/>
										</div>

										{actions && (
											<div>
												<h5>{i18n`Suggested Actions`}</h5>
												<HFTextInput
													name='actions.which'
													placeholder={i18n`Share your suggestion`}
													multiline={true}
													rows={5}
													noResize
												/>
											</div>
										)}
									</VStack>
								</fieldset>
							</form>
						</div>
					</FormProvider>
					<TaskMenuController
						data={{
							formId: id,
							options: ['postpone', 'forward'],
							submitting,
							submissionType: 'submit',
							getValues,
						}}
					/>
				</>
			)}
		</>
	)
}

export default CausalLinkEvaluationTaskController
